import { MouseEvent, useEffect, useMemo, useState } from 'react';
import { RecoilRoot } from 'recoil';

import dayjs from 'dayjs';
import { Box } from '@mui/material';

import { queries, queryKeys, mutations } from 'RestAPIs/rapini';
import { TravelMovementPerDayResponse } from 'generated/index';
import { v4 as uuidv4 } from 'uuid';

import {
  Footer,
  Header
} from 'components/roster-portal/home/travel-expense-claims/components';
import { PlanUnitMix } from 'components/types';
import { useColumnDefinitions } from './useColumnDefinitions';
import LoadingOverlay from 'components/shared/LoadingOverlay';
import { getCurrentlyLoggedInEmployeeId } from 'utils/misc';
import TravelDetailDialog from 'components/roster-portal/home/travel-expense-claims/components/TravelDetailDialog';
import utc from 'dayjs/plugin/utc';
import { t } from 'i18next';
import { calculateTotalKmByType } from 'components/roster-portal/home/travel-expense-claims/utils';
import { DataGrid } from 'components/shared/DataGrid';

import { WarningDialog } from 'components/roster-portal/activity-planning/components';
import WarningDialogAdvanced from 'components/roster-portal/activity-planning/components/WarningDialog/WarningDialogAdvanced';
import { faCheck, faClose } from 'vendors/@fortawesome/pro-light-svg-icons';
import { useQueryClient } from 'react-query';
import {
  showErrorSnackbar,
  showSuccessSnackbar
} from 'components/shared/Snackbar';
import { ClaimsAction } from 'components/roster-portal/home/travel-expense-claims';

dayjs.extend(utc);

interface ExtendedTravelMovementPerDayResponse
  extends TravelMovementPerDayResponse {
  uniqueId: string;
}

interface TravelMovementsProps {
  page?: string;
  selectedEmployeeId?: number;
  travelExchangeClaimsMonth?: Date | null;
  showActions?: boolean;
  isTravelMovementsBasedOnPlanUnit?: boolean;
  showEmployeeName?: boolean;
}

const TravelMovements = ({
  page = 'START',
  selectedEmployeeId,
  travelExchangeClaimsMonth = null,
  showActions = false,
  isTravelMovementsBasedOnPlanUnit = false,
  showEmployeeName = false
}: TravelMovementsProps) => {
  const columns = useColumnDefinitions({ showEmployeeName });
  const [selectedPlanUnits, setSelectedPlanUnits] = useState<PlanUnitMix[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(
    travelExchangeClaimsMonth || new Date()
  );
  const loggedInUserEmployeeId = +getCurrentlyLoggedInEmployeeId();
  const [selectedRows, setSelectedRows] = useState<
    ExtendedTravelMovementPerDayResponse[]
  >([]);
  const startDate = dayjs.utc(selectedDate).startOf('month').toISOString();
  const endDate = dayjs.utc(selectedDate).endOf('month').toISOString();
  const organizationalUnitIds = selectedPlanUnits
    .map((unit) => unit.id)
    .join(',');

  const [showDetailDialog, setShowDetailDialog] = useState<boolean>(false);
  const [showWarningDialog, setShowWarningDialog] = useState<
    [boolean, ClaimsAction | null]
  >([false, null]);
  const [showRejectDialog, setShowRejectDialog] = useState<
    [boolean, ClaimsAction | null]
  >([false, null]);
  const [activeClaim, setActiveClaim] =
    useState<TravelMovementPerDayResponse | null>(null);

  const isOverview = !page || page === 'START';
  const isEmployeeCard = page === 'EMPLOYEE_CARD';
  const isEmployeePortal = page === 'EMPLOYEE_PORTAL';
  const isEmployee = isEmployeeCard || isEmployeePortal;

  const queryClient = useQueryClient();

  // Expense claims by employee
  const getTravelMovementsByEmployee = () =>
    queries.useFindTravelMovementsByEmployee(
      startDate,
      endDate,
      selectedEmployeeId as number,
      {
        enabled: !!(loggedInUserEmployeeId || selectedEmployeeId)
      }
    );

  // Expense claims by organizational unit
  const getTravelMovementsByOrganizationalUnits = () =>
    queries.useFindTravelMovementsByOrganizationalUnits(
      startDate,
      endDate,
      // eslint-disable-next-line
      // @ts-ignore
      organizationalUnitIds as number[],
      {
        enabled:
          !!(loggedInUserEmployeeId || selectedEmployeeId) &&
          !isTravelMovementsBasedOnPlanUnit
      }
    );

  const {
    data = [],
    refetch: refetchTravelMovements,
    isLoading
  } = isTravelMovementsBasedOnPlanUnit
    ? getTravelMovementsByOrganizationalUnits()
    : getTravelMovementsByEmployee();

  const rows = useMemo(
    () =>
      data.map((response: TravelMovementPerDayResponse) => ({
        ...response,
        uniqueId: uuidv4(),
        disableSelection: !response.canBeApproved
      })),
    [data]
  );

  const handleCloseDetailDialog = () => {
    setShowDetailDialog(false);
    setActiveClaim(null);
  };

  const totalKmPrivate = calculateTotalKmByType(rows, 'PRIVATE');
  const totalKmBusiness = calculateTotalKmByType(rows, 'BUSINESS');

  const { mutate: approveMutation, isLoading: isLoadingApproveMutation } =
    mutations.useApproveExpenseClaims({
      onSuccess: () => {
        const queryKey = isEmployee
          ? queryKeys.findTravelMovementsByEmployee(
              startDate,
              endDate,
              selectedEmployeeId as number
            )
          : queryKeys.findTravelMovementsByOrganizationalUnits(
              startDate,
              endDate,
              organizationalUnitIds.split(',').map(Number)
            );
        queryClient.invalidateQueries(queryKey);
        refetchTravelMovements();
        showSuccessSnackbar();
        setSelectedRows([]);
        setShowWarningDialog([false, null]);
      },
      onError: (e: any) => {
        showErrorSnackbar(e.message);
      }
    });

  const { mutate: rejectMutation, isLoading: isLoadingRejectMutation } =
    mutations.useRejectExpenseClaims({
      onSuccess: () => {
        const queryKey = isEmployee
          ? queryKeys.findTravelMovementsByEmployee(
              startDate,
              endDate,
              selectedEmployeeId as number
            )
          : queryKeys.findTravelMovementsByOrganizationalUnits(
              startDate,
              endDate,
              organizationalUnitIds.split(',').map(Number)
            );
        queryClient.invalidateQueries(queryKey);
        refetchTravelMovements();
        showSuccessSnackbar();
        setSelectedRows([]);
        setShowRejectDialog([false, null]);
      },
      onError: (e: any) => {
        showErrorSnackbar(e.message);
      }
    });

  const approveClaims = () => {
    const expenseClaimIds = selectedRows
      .filter((row) => row.canBeApproved && row.id !== undefined)
      .map(({ id }) => id as string);

    approveMutation({ expenseClaimIds });
  };

  const rejectClaims = (formData: { remark: string }) => {
    const expenseClaimIds = selectedRows
      .map(({ id }) => id)
      .filter((id): id is string => id !== undefined);

    rejectMutation({ expenseClaimIds, statusNote: formData.remark });
  };

  const handleAction = (action: ClaimsAction) => {
    switch (action) {
      case ClaimsAction.APPROVE:
        setShowWarningDialog([true, ClaimsAction.APPROVE]);
        break;
      case ClaimsAction.REJECT:
        setShowRejectDialog([true, ClaimsAction.REJECT]);
        break;
      default:
        break;
    }
  };

  const handleActionClick = (
    uniqueId: string | number,
    row: ExtendedTravelMovementPerDayResponse,
    action: ClaimsAction,
    event?: MouseEvent<HTMLButtonElement>
  ) => {
    event?.preventDefault();
    event?.stopPropagation();

    row &&
      !selectedRows.some((r) => r.uniqueId === row.uniqueId) &&
      setSelectedRows((prev) => [...prev, row]);

    setActiveClaim(rows.find((r) => r.uniqueId === uniqueId)!);
    handleAction?.(action);
  };

  useEffect(() => {
    if (selectedPlanUnits.length > 0) refetchTravelMovements();
  }, [selectedPlanUnits, selectedDate]);

  return (
    <RecoilRoot>
      <Header
        onAction={handleAction}
        onSelectPlanUnit={setSelectedPlanUnits}
        onSelectedDate={setSelectedDate}
        selectedDate={dayjs(selectedDate).toDate()}
        selection={selectedRows}
        showActions={isOverview}
        showPlanUnitSelector={isOverview}
      />
      <Box>
        <DataGrid<ExtendedTravelMovementPerDayResponse>
          rows={rows}
          getRowId={(row) => row?.uniqueId}
          columns={columns}
          loading={
            isLoading || isLoadingRejectMutation || isLoadingApproveMutation
          }
          pageSize={50}
          autoHeight={true}
          rowsPerPageOptions={[5]}
          checkboxSelection={rows.some(
            ({ disableSelection }) => !disableSelection
          )}
          customSelectionModel={selectedRows.map(({ uniqueId }) => uniqueId)}
          onSelectionChange={(selectedRowIds: (string | number)[]) => {
            const selectedRowsData = rows.filter((row) =>
              selectedRowIds.includes(row.uniqueId)
            );
            setSelectedRows(selectedRowsData);
          }}
          footer={
            <Footer>
              <Box mr={1}>
                {`${t('travelMovements.totalKm')}: ${totalKmPrivate} (${t(
                  'travelMovements.type.PRIVATE'
                )}) 
                - ${totalKmBusiness} (${t('travelMovements.type.BUSINESS')})`}
              </Box>
            </Footer>
          }
          onViewClick={(
            id: string | number,
            _row,
            event?: MouseEvent<HTMLButtonElement>
          ) => {
            event?.preventDefault();
            event?.stopPropagation();
            setActiveClaim(rows.find((r) => r.uniqueId === id)!);
            setShowDetailDialog(true);
          }}
          customActions={(id, row) => [
            {
              tooltipTitle: t('travelExpenseClaims.actions.APPROVE'),
              icon: faCheck,
              onClick: (id, _row, event) =>
                row && handleActionClick(id, row, ClaimsAction.APPROVE, event),
              hidden: !showActions || row?.status !== 'SUBMITTED'
            },
            {
              tooltipTitle: t('travelExpenseClaims.actions.REJECT'),
              icon: faClose,
              onClick: (id, _row, event) =>
                row && handleActionClick(id, row, ClaimsAction.REJECT, event),
              hidden: !showActions || row?.status !== 'SUBMITTED'
            }
          ]}
          initialState={{
            sorting: {
              sortModel: [{ field: 'date', sort: 'asc' }]
            }
          }}
          sx={{
            mt: 1,
            // Hide the checkbox if the row/input is disabled
            '.MuiCheckbox-root input[type="checkbox"]:disabled + *': {
              display: 'none'
            }
          }}
        />
      </Box>

      <WarningDialog
        title={t('travelExpenseClaims.warning', {
          action: t(`travelExpenseClaims.actions.${showWarningDialog[1]}`)
        })}
        open={showWarningDialog[0]}
        onSubmit={() => approveClaims()}
        onCancel={() => {
          setShowWarningDialog([false, null]);
        }}
      />

      <WarningDialogAdvanced
        title={t('travelExpenseClaims.warning', {
          action: t(`travelExpenseClaims.actions.${showRejectDialog[1]}`)
        })}
        open={showRejectDialog[0]}
        selectedRows={selectedRows}
        onSubmit={(formData) => rejectClaims(formData)}
        onCancel={() => {
          setShowRejectDialog([false, null]);
        }}
      />

      <TravelDetailDialog
        claim={activeClaim}
        isOpen={showDetailDialog}
        onClose={handleCloseDetailDialog}
        isTravelMovements={true}
      />

      <LoadingOverlay loading={isLoading} />
    </RecoilRoot>
  );
};

export default TravelMovements;
